import { renderElementToPDF } from './elementRenderer.jsx';
import { preloadImages, getPreloadedImage } from './imagePreloader.js';

/**
 * Renders a book spread to PDF with concurrent image loading optimization
 */
export async function renderSpreadToPDF(doc, spread, options = {}) {
  if (!spread) {
    console.warn('Spread is undefined, skipping render');
    return;
  }

  const { 
    scale = 1, 
    scaleY, 
    isCoverSpread = false,
    offsetX = 0,
    offsetY = 0,
    bleed = 0,
    includeBleed = true,
    hasPageNumbers = false,
    spreadNumber,
    width,
    height
  } = options;
  
  const yScale = scaleY || scale;
  const finalBleedOffsetX = includeBleed ? bleed : 0;
  const finalBleedOffsetY = includeBleed ? bleed : 0;

  // Get elements from spread
  const elements = spread.elements || [];
  if (elements.length === 0) {
    console.warn('No elements found in spread');
    return;
  }

  // Ensure all elements have a type
  elements.forEach(element => {
    if (!element.type) {
      // Try to infer type from properties
      if (element.content || element.text) {
        element.type = 'text';
      } else if (element.src || element.imagePrompt) {
        element.type = 'image';
      } else if (element.shape || element.shapeType || element.style?.fill) {
        element.type = 'shape';
      } else {
        element.type = 'text'; // Default to text
      }
    }
  });

  // Create border shape elements for images with borders
  const borderShapes = [];
  elements.forEach(element => {
    if (element.type === 'image' && element.borderRadius && element.hasBorder) {
      const borderShape = {
        id: `${element.id}-border`,
        type: 'shape',
        shapeType: 'roundedRect',
        position: { ...element.position },
        size: { ...element.size },
        style: {
          fill: element.borderColor || '#ffffff',
          stroke: element.borderColor || '#ffffff',
          strokeWidth: element.borderWidth || 8,
          borderRadius: element.borderRadius,
          zIndex: (element.style?.zIndex || element.zIndex || 0) + 0.5
        },
        borderRadius: element.borderRadius,
        borderStyle: element.borderStyle || 'solid',
        borderWidth: element.borderWidth || 8,
        borderColor: element.borderColor || '#ffffff',
        borderOpacity: element.borderOpacity !== undefined ? element.borderOpacity : 100,
        _isBorderShape: true
      };
      borderShapes.push(borderShape);
      console.log('[PDF Border] Created border shape for image:', element.id, 'borderRadius:', element.borderRadius, 'borderStyle:', element.borderStyle);
    }
  });

  // Combine all elements (originals + auto-generated borders)
  const allElements = [...elements, ...borderShapes];

  // Sort ALL elements by z-index only - respect user's layering arrangement
  const sortedElements = allElements.sort((a, b) => {
    const aZIndex = a.style?.zIndex || a.zIndex || 0;
    const bZIndex = b.style?.zIndex || b.zIndex || 0;
    return aZIndex - bZIndex; // Lower z-index rendered first (bottom layer)
  });

  let preloadedImages = null;
  if (!options.omitImages) {
    // **OPTIMIZATION: Preload all images concurrently before rendering**
    console.log('🚀 Starting concurrent image preloading...');
    preloadedImages = await preloadImages(sortedElements, {
      flattenTransparency: false,
      timeout: 10000,
      onProgress: (loaded, total) => {
        console.log(`📸 Image preloading progress: ${loaded}/${total}`);
      }
    });
    console.log(`✅ Image preloading completed. Rendering ${sortedElements.length} elements...`);
  } else {
    console.log('⏭️ Skipping image preloading (omitImages enabled)');
  }

  // Render elements sequentially (but images are already loaded)
  for (const element of sortedElements) {
    try {
      if (!element) continue;

      // Ensure element has type
      if (!element.type) {
        if (element.content || element.text) {
          element.type = 'text';
        } else if (element.src || element.imagePrompt) {
          element.type = 'image';
        } else if (element.shape || element.shapeType || element.style?.fill) {
          element.type = 'shape';
        } else {
          continue;
        }
      }
      
      // Apply position adjustments
      const adjustedElement = {
        ...element,
        position: {
          x: (element.position.x + offsetX) + finalBleedOffsetX,
          y: (element.position.y + offsetY) + finalBleedOffsetY
        }
      };
      
      // For image elements, attach the preloaded image
      if (element.type === 'image' && element.src) {
        const preloadedImage = getPreloadedImage(preloadedImages, element.src);
        if (preloadedImage) {
          adjustedElement._preloadedImage = preloadedImage;
          console.log(`📸 Using preloaded image for element ${element.id}`);
        }
      }
      
      // Render the element
      await renderElementToPDF(doc, adjustedElement, {
        scale,
        yScale,
        debug: options.debug,
        orientation: options.orientation || 'portrait',
        pageSize: options.pageSize || 'us-trade',
        luluCompatibility: options.luluCompatibility || {},
        flippedY: true,
        omitImages: options.omitImages
      });
    } catch (elementError) {
      console.error(`Error rendering element ${element.id || 'unknown'}:`, elementError);
    }
  }

  // Add page numbers if enabled (not for cover spreads)
  if (hasPageNumbers && spreadNumber && !isCoverSpread) {
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100); // Gray color for page numbers
    doc.setFont('Baskervville-Regular', 'normal');
    
    // Calculate page dimensions
    const pageWidth = width || doc.internal.pageSize.getWidth() / 2; // Half width for each page in spread
    const pageHeight = height || doc.internal.pageSize.getHeight();
    
    // Left page number (odd)
    const leftPageNumber = spreadNumber * 2 - 1;
    doc.text(
      leftPageNumber.toString(), 
      pageWidth / 2, 
      pageHeight - 28,
      { align: 'center' }
    );
    
    // Right page number (even)
    const rightPageNumber = spreadNumber * 2;
    doc.text(
      rightPageNumber.toString(), 
      pageWidth + (pageWidth / 2), 
      pageHeight - 28,
      { align: 'center' }
    );
  }
}
